/**
 * The "Switch tenant" row of the Employee account menu and the submenu that it opens.
 *
 * 1. The row shows the action label and, as secondary text, the name of the active Organization.
 * 2. A click on the row opens a submenu next to the row. The submenu holds the
 *    `OrganizationSwitcherItems` rows, one row for each Membership.
 * 3. A pick in the submenu runs the switch flow of `OrganizationSwitcherItems`, then closes the
 *    submenu and the account menu.
 *
 * The layout copies `AccountMenuLanguageItem`, the other account menu row that opens a submenu.
 * The rows stay in `OrganizationSwitcherItems` because `OrganizationSwitcherButton` on the
 * onboarding shell shows the same rows.
 */
import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { ArrowRightIcon, OfficeIcon } from '@modernloop/shared/icons';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import OrganizationSwitcherItems from 'src/components/OrganizationSwitcher/OrganizationSwitcherItems';
import { useOrganizationMemberships } from 'src/components/OrganizationSwitcher/useOrganizationMemberships';

type Props = {
  /** Called after the user picks an Organization, so the account menu can close itself. */
  onSelect: () => void;
};

/**
 * Opens the Organization switcher from the account menu.
 *
 * Renders nothing when `useOrganizationMemberships` answers fewer than two Memberships, because
 * then the user has no Organization to switch to.
 */
const AccountMenuSwitchTenantItem = ({ onSelect }: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const memberships = useOrganizationMemberships();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLLIElement>(null);

  if (memberships.length < 2) {
    return null;
  }

  const activeOrganizationName = memberships.find((membership) => membership.isActive)?.organization.name;

  const handleSelectOrganization = () => {
    setOpen(false);
    onSelect();
  };

  return (
    <>
      <MenuItem
        ref={anchorRef}
        id="account-menu-switch-tenant"
        data-testid="account-menu-switch-tenant"
        onClick={() => setOpen(true)}
      >
        <ListItemIcon>
          <OfficeIcon />
        </ListItemIcon>
        {/* The user asked for the word "tenant" in this label. Code and comments say Organization. */}
        <ListItemText
          primary={t('hiring-dashboard-nav:nav.organizationSwitcher.switchTenant')}
          secondary={activeOrganizationName}
        />
        <ArrowRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 248 } } }}
      >
        <OrganizationSwitcherItems onSelect={handleSelectOrganization} />
      </Menu>
    </>
  );
};

export default AccountMenuSwitchTenantItem;
